import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import * as XLSX from 'xlsx';

const OLD_PACKAGE_PRICES = {
  '3 Kişi 2 Gece - 3 Silahşörler': 13000,
  '2 Kişi 2 Gece - Hansel ve Gretel': 13600,
  "1 Kişi 2 Gece - Alice'in Anahtarı": 17000,
  '3 Kişi 1 Gece Cuma - Aslan, Cadı ve Dolap': 7500,
  '3 Kişi 1 Gece Cumartesi - Camelot Konseyi': 9800,
  '2 Kişi 1 Gece Cuma - Küçük Prens ve Tilki': 7900,
  '2 Kişi 1 Gece Cumartesi - Ay Işığı Balosu': 10200,
  "1 Kişi 1 Gece Cuma - Rapunzel'in Kulesi": 9500,
  '1 Kişi 1 Gece Cumartesi - Büyülü Balkabağı': 11750,
};

const NEW_PACKAGE_PRICES = {
  '3 Kişi 2 Gece - 3 Silahşörler': 13800,
  '2 Kişi 2 Gece - Hansel ve Gretel': 14300,
  "1 Kişi 2 Gece - Alice'in Anahtarı": 17700,
  '3 Kişi 1 Gece Cuma - Aslan, Cadı ve Dolap': 8000,
  '3 Kişi 1 Gece Cumartesi - Camelot Konseyi': 10250,
  '2 Kişi 1 Gece Cuma - Küçük Prens ve Tilki': 8900,
  '2 Kişi 1 Gece Cumartesi - Ay Işığı Balosu': 11000,
  "1 Kişi 1 Gece Cuma - Rapunzel'in Kulesi": 10750,
  '1 Kişi 1 Gece Cumartesi - Büyülü Balkabağı': 13000,
};

const EXTRA_PACKAGES = [
  "1 Kişi 1 Gece - Peter Pan'ın Gölgesi",
  "2 Kişi 1 Gece - Ariel'in Uykusu",
  "3 Kişi 1 Gece - Teneke Adamın Kalbi"
];

const getNetAmount = (r) => {
  const choice = r.booking?.palace_choice;
  const isExtra = EXTRA_PACKAGES.includes(choice);
  
  if (isExtra) {
    // Ekstra kalemlerde "Bize Kalan", Kredi Kartı ise komisyon düşülmüş hali, Havale ise direkt tutardır.
    if (r.booking?.payment_method === 'credit card-tek') {
      return (r.amount || 0) * 0.9631;
    }
    return r.amount || 0;
  }

  const createdAt = new Date(r.created_at);
  const cutoffOld = new Date('2026-03-31T00:00:00');
  const cutoffNew = new Date('2026-04-06T11:20:00+03:00');
  
  if (createdAt < cutoffOld) return r.amount || 0;
  if (createdAt < cutoffNew) return OLD_PACKAGE_PRICES[choice] || 0;
  return NEW_PACKAGE_PRICES[choice] || 0;
};

const STATUS_CONFIG = {
  receipt_pending: { label: 'Dekont Bekleniyor', badge: 'warning text-dark' },
  approved:        { label: 'Onaylandı',         badge: 'success' },
  declined:        { label: 'Reddedildi',        badge: 'danger' },
  pending:         { label: 'Beklemede',          badge: 'info' },
  failed:          { label: 'Başarısız',          badge: 'secondary' },
  no_payment:      { label: 'Ödeme Yok',         badge: 'dark' },
  deleted:         { label: 'Silindi',           badge: 'secondary' },
};

const Field = ({ label, value, full = false }) => (
  <div className={full ? 'col-12' : 'col-md-6 col-12'}>
    <div className="p-2 rounded" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
      <small className="text-muted d-block mb-1" style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</small>
      <span style={{ fontSize: '0.9rem', wordBreak: 'break-word' }}>{value || <span className="text-muted">—</span>}</span>
    </div>
  </div>
);

export default function Admin() {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [clubFilter, setClubFilter] = useState('');
  const [termFilter, setTermFilter] = useState('');
  const [transportFilter, setTransportFilter] = useState('');
  const [entryFilter, setEntryFilter] = useState('');
  const [exitFilter, setExitFilter] = useState('');
  const [packageFilter, setPackageFilter] = useState('');
  const [paymentTypeFilter, setPaymentTypeFilter] = useState('');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [actionLoading, setActionLoading] = useState('');
  const [showDuplicatesOnly, setShowDuplicatesOnly] = useState(false);
  const [showDeletedOnly, setShowDeletedOnly] = useState(false);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('orders')
      .select('*, bookings(*)')
      .order('created_at', { ascending: false });
    if (!error && data) {
      setRecords(data.map((order) => ({
        ...order,
        booking: order.bookings?.[0] || null,
      })));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) navigate('/admin/giris');
      else fetchRecords();
    });
  }, [navigate, fetchRecords]);

  const updateStatus = async (orderId, newStatus) => {
    setActionLoading(orderId + newStatus);
    await supabase.from('orders').update({ status: newStatus, updated_at: new Date().toISOString() }).eq('order_id', orderId);
    await fetchRecords();
    setSelectedRecord(null);
    setActionLoading('');
  };

  const deleteRecord = async (orderId) => {
    if (!window.confirm(!showDeletedOnly ? 'Bu kaydı çöpe taşımak istediğinizden emin misiniz?' : 'Bu kaydı kalıcı olarak silmek istediğinizden emin misiniz? Dikkat, bu işlem geri alınamaz!')) return;
    setActionLoading(orderId + 'delete');
    if (!showDeletedOnly) {
      await supabase.from('orders').update({ status: 'deleted', updated_at: new Date().toISOString() }).eq('order_id', orderId);
    } else {
      await supabase.from('orders').delete().eq('order_id', orderId);
    }
    await fetchRecords();
    setSelectedRecord(null);
    setActionLoading('');
  };

  const restoreRecord = async (orderId) => {
    setActionLoading(orderId + 'restore');
    await supabase.from('orders').update({ status: 'pending', updated_at: new Date().toISOString() }).eq('order_id', orderId);
    await fetchRecords();
    setSelectedRecord(null);
    setActionLoading('');
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/admin/giris');
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setClubFilter('');
    setTermFilter('');
    setTransportFilter('');
    setEntryFilter('');
    setExitFilter('');
    setPackageFilter('');
    setPaymentTypeFilter('');
    setShowDuplicatesOnly(false);
    setShowDeletedOnly(false);
  };

  const exportToExcel = () => {
    const rows = filtered.map((r) => ({
      'Sipariş No':    r.order_id,
      'Ad Soyad':      r.booking?.full_name || '',
      'E-posta':       r.booking?.email || r.customer_email || '',
      'Alt E-posta':   r.booking?.alt_email || '',
      'Telefon':       r.booking?.phone_number || r.customer_phone || '',
      'TC / Pasaport': r.booking?.passport_id || '',
      'Kulüp':         r.booking?.club_name || '',
      'Dönem Görevi':  r.booking?.term_assignment || '',
      'Paket':         r.booking?.palace_choice || '',
      'Tutar (TRY)':   r.amount || '',
      'Para Birimi':   r.currency || 'TRY',
      'Ulaşım':        r.booking?.transportation || '',
      'Giriş Günü':    r.booking?.early_stay || '',
      'Çıkış Günü':    r.booking?.checkout_day || '',
      'Oda Arkadaşı':  r.booking?.roommate || '',
      'Acil Kişi':     r.booking?.emergency_contact_name || '',
      'Acil Tel':      r.booking?.emergency_contact_phone || '',
      'Ek Notlar':     r.booking?.additional_notes || '',
      'KVKK Onayı':    r.booking?.kvkk_consent ? 'Evet' : 'Hayır',
      'Ödeme Yöntemi': r.booking?.payment_method || '',
      'Dekont URL':    r.booking?.payment_receipt_url || '',
      'Durum':         STATUS_CONFIG[r.status]?.label || r.status,
      'Kayıt Tarihi':  new Date(r.created_at).toLocaleString('tr-TR'),
      'Güncelleme':    new Date(r.updated_at).toLocaleString('tr-TR'),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Kayıtlar');
    XLSX.writeFile(wb, `konferans-kayitlar-${Date.now()}.xlsx`);
  };

  const activeRecords = showDeletedOnly
    ? records.filter(r => r.status === 'deleted')
    : records.filter(r => r.status !== 'deleted');

  const nameCounts = activeRecords.reduce((acc, r) => {
    const name = (r.booking?.full_name || r.customer_name || '').toLowerCase().trim();
    if (name) acc[name] = (acc[name] || 0) + 1;
    return acc;
  }, {});
  const duplicateNames = Object.keys(nameCounts).filter(n => nameCounts[n] > 1);

  const filtered = activeRecords.filter((r) => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      (r.booking?.full_name || '').toLowerCase().includes(q) ||
      (r.booking?.email || r.customer_email || '').toLowerCase().includes(q) ||
      (r.booking?.club_name || '').toLowerCase().includes(q) ||
      (r.booking?.passport_id || '').toLowerCase().includes(q) ||
      (r.booking?.phone_number || '').toLowerCase().includes(q) ||
      r.order_id.toLowerCase().includes(q);
    
    const matchStatus = !statusFilter || r.status === statusFilter;
    const matchClub = !clubFilter || r.booking?.club_name === clubFilter;
    const matchTerm = !termFilter || r.booking?.term_assignment === termFilter;
    const matchTransport = !transportFilter || r.booking?.transportation === transportFilter;
    const matchEntry = !entryFilter || r.booking?.early_stay === entryFilter;
    const matchExit = !exitFilter || r.booking?.checkout_day === exitFilter;
    const matchPackage = !packageFilter || r.booking?.palace_choice === packageFilter;
    const matchPaymentType = !paymentTypeFilter || 
      (paymentTypeFilter === 'credit-card' ? r.booking?.payment_method === 'credit card-tek' : r.booking?.payment_method !== 'credit card-tek');

    const name = (r.booking?.full_name || r.customer_name || '').toLowerCase().trim();
    const matchDuplicates = !showDuplicatesOnly || duplicateNames.includes(name);

    return matchSearch && matchStatus && matchClub && matchTerm && matchTransport && matchEntry && matchExit && matchPackage && matchPaymentType && matchDuplicates;
  });

  const getUnique = (field) => {
    const values = activeRecords.map(r => r.booking?.[field]).filter(v => v && v.trim() !== '');
    return Array.from(new Set(values)).sort();
  };

  const filterOptions = {
    clubs:      getUnique('club_name'),
    terms:      getUnique('term_assignment'),
    transports: getUnique('transportation'),
    entries:    getUnique('early_stay'),
    exits:      getUnique('checkout_day'),
    packages:   getUnique('palace_choice')
  };

  const activeRecordsStats = records.filter(r => r.status !== 'deleted');

  const stats = {
    total:       activeRecordsStats.length,
    pending:     activeRecordsStats.filter((r) => r.status === 'receipt_pending' || r.status === 'pending').length,
    approved:    activeRecordsStats.filter((r) => r.status === 'approved').length,
    
    // Ayrıştırılmış Onaylanan Sayıları
    standardApproved: activeRecordsStats.filter((r) => r.status === 'approved' && !EXTRA_PACKAGES.includes(r.booking?.palace_choice)).length,
    extraApproved:    activeRecordsStats.filter((r) => r.status === 'approved' && EXTRA_PACKAGES.includes(r.booking?.palace_choice)).length,

    declined:    activeRecordsStats.filter((r) => r.status === 'declined' || r.status === 'failed').length,
    totalAmount:    activeRecordsStats.filter((r) => r.status === 'approved').reduce((s, r) => s + (r.amount || 0), 0),
    totalNetAmount: activeRecordsStats.filter((r) => r.status === 'approved').reduce((s, r) => s + getNetAmount(r), 0),
    
    // Credit Card Stats
    ccTotal:     activeRecordsStats.filter((r) => r.booking?.payment_method === 'credit card-tek').length,
    ccPending:   activeRecordsStats.filter((r) => (r.status === 'receipt_pending' || r.status === 'pending') && r.booking?.payment_method === 'credit card-tek').length,
    ccApproved:  activeRecordsStats.filter((r) => r.status === 'approved' && r.booking?.payment_method === 'credit card-tek').length,
    ccAmount:    activeRecordsStats.filter((r) => r.status === 'approved' && r.booking?.payment_method === 'credit card-tek').reduce((s, r) => s + (r.amount || 0), 0),
    
    // Cash/Manual Stats
    cashTotal:   activeRecordsStats.filter((r) => r.booking?.payment_method !== 'credit card-tek').length,
    cashPending: activeRecordsStats.filter((r) => (r.status === 'receipt_pending' || r.status === 'pending') && r.booking?.payment_method !== 'credit card-tek').length,
    cashApproved: activeRecordsStats.filter((r) => r.status === 'approved' && r.booking?.payment_method !== 'credit card-tek').length,
    cashAmount:  activeRecordsStats.filter((r) => r.status === 'approved' && r.booking?.payment_method !== 'credit card-tek').reduce((s, r) => s + (r.amount || 0), 0),
  };

  const b = selectedRecord?.booking;

  return (
    <div className="admin-panel-context" style={{ backgroundColor: '#f1f3f5', minHeight: '100vh' }}>

      {/* Navbar */}
      <nav className="navbar navbar-dark px-4 py-3" style={{ backgroundColor: '#220d1c' }}>
        <span className="navbar-brand fw-bold">Admin Paneli</span>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-light btn-sm" onClick={fetchRecords}>Yenile</button>
          <button className="btn btn-outline-warning btn-sm" onClick={exportToExcel}>Excel İndir</button>
          <button className="btn btn-outline-danger btn-sm" onClick={handleLogout}>Çıkış</button>
        </div>
      </nav>

      <div className="container-fluid px-4 py-4">

        {/* General Stats */}
        <div className="row g-3 mb-4">
          <div className="col-12">
            <div className="py-2 px-3 rounded-pill d-inline-block mb-1 shadow-sm" style={{ backgroundColor: '#220d1c', border: '1px solid rgba(255,255,255,0.1)' }}>
              <span className="fw-bold" style={{ color: '#fff', fontSize: '0.85rem' }}>🌍 Genel Özet</span>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card text-center p-4 h-100 shadow-sm border-0" style={{ backgroundColor: '#fff', borderRadius: '16px' }}>
              <div className="h2 mb-1 fw-bold" style={{ color: '#220d1c' }}>{stats.total}</div>
              <div className="text-muted small text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>Toplam Kayıt</div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card text-center p-4 h-100 shadow-sm border-0" style={{ backgroundColor: '#fff', borderRadius: '16px', borderTop: '4px solid #db9e37' }}>
              <div className="h2 mb-1 fw-bold" style={{ color: '#db9e37' }}>{stats.pending}</div>
              <div className="text-muted small text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>Toplam Bekleyen Kayıt</div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card text-center p-4 h-100 shadow-sm border-0" style={{ backgroundColor: '#fff', borderRadius: '16px', borderTop: '4px solid #4ade80' }}>
              <div className="h2 mb-1 fw-bold" style={{ color: '#4ade80' }}>{stats.standardApproved}</div>
              <div className="text-muted small text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>Standart Onaylanan</div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card text-center p-4 h-100 shadow-sm border-0" style={{ backgroundColor: '#fff', borderRadius: '16px', borderTop: '4px solid #a855f7' }}>
              <div className="h2 mb-1 fw-bold" style={{ color: '#a855f7' }}>{stats.extraApproved}</div>
              <div className="text-muted small text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>Ekstra Konaklama Onaylanan</div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card text-center p-4 h-100 shadow-sm border-0" style={{ backgroundColor: '#fff', borderRadius: '16px', borderTop: '4px solid #f87171' }}>
              <div className="h2 mb-1 fw-bold" style={{ color: '#f87171' }}>{stats.declined}</div>
              <div className="text-muted small text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>Toplam Reddedilen Kayıt</div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card text-center p-4 h-100 shadow-sm border-0" style={{ backgroundColor: '#fff', borderRadius: '16px', borderTop: '4px solid #4ade80' }}>
              <div className="h2 mb-1 fw-bold" style={{ color: '#4ade80' }}>₺{stats.totalAmount.toLocaleString('tr-TR')}</div>
              <div className="text-muted small text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>Toplam Tahsil Edilen</div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card text-center p-4 h-100 shadow-sm border-0" style={{ backgroundColor: '#fff', borderRadius: '16px', borderTop: '4px solid #60a5fa' }}>
              <div className="h2 mb-1 fw-bold" style={{ color: '#60a5fa' }}>₺{stats.totalNetAmount.toLocaleString('tr-TR')}</div>
              <div className="text-muted small text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>Bize Kalan (Paket Fiyatı)</div>
            </div>
          </div>
        </div>

        {/* CC Stats Breakdown */}
        <div className="row g-3 mb-4">
          <div className="col-12">
            <div className="py-2 px-3 rounded-pill d-inline-block mb-1 shadow-sm" style={{ backgroundColor: 'rgba(116, 38, 64, 0.1)', border: '1px solid rgba(116, 38, 64, 0.2)' }}>
              <span className="fw-bold" style={{ color: '#742640', fontSize: '0.85rem' }}>💳 Kredi Kartı Detayları</span>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card text-center p-3 h-100 border-0 shadow-sm" style={{ borderLeft: '4px solid #742640', borderRadius: '12px', backgroundColor: 'rgba(116, 38, 64, 0.03)' }}>
              <div className="h4 mb-1 fw-bold" style={{ color: '#742640' }}>{stats.ccTotal}</div>
              <div className="text-muted small text-uppercase fw-bold" style={{ fontSize: '0.6rem', letterSpacing: '0.05em' }}>Kredi Kartı Toplam</div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card text-center p-3 h-100 border-0 shadow-sm" style={{ borderLeft: '4px solid #db9e37', borderRadius: '12px', backgroundColor: 'rgba(219, 158, 55, 0.03)' }}>
              <div className="h4 mb-1 fw-bold" style={{ color: '#db9e37' }}>{stats.ccPending}</div>
              <div className="text-muted small text-uppercase fw-bold" style={{ fontSize: '0.6rem', letterSpacing: '0.05em' }}>Kredi Kartı Bekleyen</div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card text-center p-3 h-100 border-0 shadow-sm" style={{ borderLeft: '4px solid #4ade80', borderRadius: '12px', backgroundColor: 'rgba(74, 222, 128, 0.03)' }}>
              <div className="h4 mb-1 fw-bold" style={{ color: '#4ade80' }}>{stats.ccApproved}</div>
              <div className="text-muted small text-uppercase fw-bold" style={{ fontSize: '0.6rem', letterSpacing: '0.05em' }}>Kredi Kartı Onaylanan</div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card text-center p-3 h-100 border-0 shadow-sm" style={{ borderLeft: '4px solid #4ade80', borderRadius: '12px', backgroundColor: 'rgba(74, 222, 128, 0.03)' }}>
              <div className="h4 mb-1 fw-bold" style={{ color: '#4ade80' }}>₺{stats.ccAmount.toLocaleString('tr-TR')}</div>
              <div className="text-muted small text-uppercase fw-bold" style={{ fontSize: '0.6rem', letterSpacing: '0.05em' }}>Kredi Kartı Tutar</div>
            </div>
          </div>
        </div>

        {/* Cash/Manual Stats Breakdown */}
        <div className="row g-3 mb-4">
          <div className="col-12">
            <div className="py-2 px-3 rounded-pill d-inline-block mb-1 shadow-sm" style={{ backgroundColor: 'rgba(219, 158, 55, 0.1)', border: '1px solid rgba(219, 158, 55, 0.2)' }}>
              <span className="fw-bold" style={{ color: '#db9e37', fontSize: '0.85rem' }}>💰 Havale Detayları</span>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card text-center p-3 h-100 border-0 shadow-sm" style={{ borderLeft: '4px solid #742640', borderRadius: '12px', backgroundColor: 'rgba(116, 38, 64, 0.03)' }}>
              <div className="h4 mb-1 fw-bold" style={{ color: '#742640' }}>{stats.cashTotal}</div>
              <div className="text-muted small text-uppercase fw-bold" style={{ fontSize: '0.6rem', letterSpacing: '0.05em' }}>Havale Toplam</div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card text-center p-3 h-100 border-0 shadow-sm" style={{ borderLeft: '4px solid #db9e37', borderRadius: '12px', backgroundColor: 'rgba(219, 158, 55, 0.03)' }}>
              <div className="h4 mb-1 fw-bold" style={{ color: '#db9e37' }}>{stats.cashPending}</div>
              <div className="text-muted small text-uppercase fw-bold" style={{ fontSize: '0.6rem', letterSpacing: '0.05em' }}>Havale Bekleyen</div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card text-center p-3 h-100 border-0 shadow-sm" style={{ borderLeft: '4px solid #4ade80', borderRadius: '12px', backgroundColor: 'rgba(74, 222, 128, 0.03)' }}>
              <div className="h4 mb-1 fw-bold" style={{ color: '#4ade80' }}>{stats.cashApproved}</div>
              <div className="text-muted small text-uppercase fw-bold" style={{ fontSize: '0.6rem', letterSpacing: '0.05em' }}>Havale Onaylanan</div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card text-center p-3 h-100 border-0 shadow-sm" style={{ borderLeft: '4px solid #4ade80', borderRadius: '12px', backgroundColor: 'rgba(74, 222, 128, 0.03)' }}>
              <div className="h4 mb-1 fw-bold" style={{ color: '#4ade80' }}>₺{stats.cashAmount.toLocaleString('tr-TR')}</div>
              <div className="text-muted small text-uppercase fw-bold" style={{ fontSize: '0.6rem', letterSpacing: '0.05em' }}>Havale Tutar</div>
            </div>
          </div>
        </div>

        {/* Search & Filter */}
        <div className="card mb-4">
          <div className="card-body">
            <div className="row g-2">
              <div className="col-md-7">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Ara (Ad, e-posta, kulüp, TC, tel, sipariş no)..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="col-md-2">
                <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="">Tüm Durumlar</option>
                  {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                    <option key={key} value={key}>{val.label}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <select className="form-select" value={paymentTypeFilter} onChange={(e) => setPaymentTypeFilter(e.target.value)}>
                  <option value="">Tüm Ödeme Türleri</option>
                  <option value="credit-card">Kredi Kartı</option>
                  <option value="manual">Havale</option>
                </select>
              </div>
              <div className="col-md-2">
                <button className="btn btn-outline-secondary w-100" onClick={clearFilters}>Filtreleri Temizle</button>
              </div>
            </div>

            {/* Sub-filters row */}
            <div className="row g-2 mt-2">
              <div className="col-12 d-flex justify-content-end align-items-center mb-2 gap-3" style={{ flexWrap: 'wrap' }}>
                <div className="form-check form-switch pb-1">
                  <input className="form-check-input" type="checkbox" id="duplicateFilter" checked={showDuplicatesOnly} onChange={(e) => setShowDuplicatesOnly(e.target.checked)} style={{ cursor: 'pointer' }} disabled={showDeletedOnly} />
                  <label className="form-check-label fw-bold" htmlFor="duplicateFilter" style={{ cursor: 'pointer', color: showDuplicatesOnly ? '#742640' : 'inherit', opacity: showDeletedOnly ? 0.5 : 1 }}>
                    Mükerrer Kayıtları Göster ({showDeletedOnly ? 0 : duplicateNames.length})
                  </label>
                </div>
                <div className="form-check form-switch pb-1">
                  <input className="form-check-input" type="checkbox" id="deletedFilter" checked={showDeletedOnly} onChange={(e) => setShowDeletedOnly(e.target.checked)} style={{ cursor: 'pointer' }} />
                  <label className="form-check-label fw-bold" htmlFor="deletedFilter" style={{ cursor: 'pointer', color: showDeletedOnly ? '#dc3545' : 'inherit' }}>
                    🗑️ Çöp Kutusu (Silinenler)
                  </label>
                </div>
              </div>
              <div className="col-md-2 col-6">
                <select className="form-select form-select-sm" value={clubFilter} onChange={(e) => setClubFilter(e.target.value)}>
                  <option value="">Tüm Kulüpler</option>
                  {filterOptions.clubs.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div className="col-md-2 col-6">
                <select className="form-select form-select-sm" value={termFilter} onChange={(e) => setTermFilter(e.target.value)}>
                  <option value="">Dönem Görevi</option>
                  {filterOptions.terms.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div className="col-md-2 col-6">
                <select className="form-select form-select-sm" value={transportFilter} onChange={(e) => setTransportFilter(e.target.value)}>
                  <option value="">Ulaşım</option>
                  {filterOptions.transports.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div className="col-md-2 col-6">
                <select className="form-select form-select-sm" value={entryFilter} onChange={(e) => setEntryFilter(e.target.value)}>
                  <option value="">Giriş Günü</option>
                  {filterOptions.entries.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div className="col-md-2 col-6">
                <select className="form-select form-select-sm" value={exitFilter} onChange={(e) => setExitFilter(e.target.value)}>
                  <option value="">Çıkış Günü</option>
                  {filterOptions.exits.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div className="col-md-2 col-6">
                <select className="form-select form-select-sm" value={packageFilter} onChange={(e) => setPackageFilter(e.target.value)}>
                  <option value="">Paket</option>
                  {filterOptions.packages.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border" role="status" />
            <p className="mt-2 text-muted">Yükleniyor...</p>
          </div>
        ) : (
          <div className="card">
            <div className="table-responsive">
              <table className="table table-hover table-sm mb-0" style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                <thead className="table-dark">
                  <tr>
                    <th>#</th>
                    <th>Ad Soyad</th>
                    <th>E-posta</th>
                    <th>Telefon</th>
                    <th>TC / Pasaport</th>
                    <th>Kulüp</th>
                    <th>Dönem Görevi</th>
                    <th>Paket</th>
                    <th>Tutar</th>
                    <th>Bize Kalan</th>
                    <th>Ulaşım</th>
                    <th>Giriş</th>
                    <th>Çıkış</th>
                    <th>Oda Arkadaşı</th>
                    <th>Acil Kişi</th>
                    <th>Acil Tel</th>
                    <th>Ödeme</th>
                    <th>KVKK</th>
                    <th>Durum</th>
                    <th>Dekont</th>
                    <th>Tarih</th>
                    <th>İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={22} className="text-center py-4 text-muted">Kayıt bulunamadı.</td>
                    </tr>
                  ) : filtered.map((r, i) => {
                    let sc = STATUS_CONFIG[r.status] || { label: r.status, badge: 'secondary' };
                    if ((r.status === 'receipt_pending' || r.status === 'pending') && r.booking?.payment_method === 'credit card-tek') {
                      sc = { ...sc, label: 'Onay Bekleniyor' };
                    }
                    return (
                      <tr key={r.id}>
                        <td className="text-muted">{i + 1}</td>
                        <td>
                          <button className="btn btn-link p-0 text-start" style={{ fontSize: '0.82rem' }} onClick={() => setSelectedRecord(r)}>
                            {r.booking?.full_name || r.customer_name || '—'}
                          </button>
                        </td>
                        <td>{r.booking?.email || r.customer_email || '—'}</td>
                        <td>{r.booking?.phone_number || r.customer_phone || '—'}</td>
                        <td>{r.booking?.passport_id || '—'}</td>
                        <td>{r.booking?.club_name || '—'}</td>
                        <td>{r.booking?.term_assignment || '—'}</td>
                        <td>{r.booking?.palace_choice || '—'}</td>
                        <td>₺{(r.amount || 0).toLocaleString('tr-TR')}</td>
                        <td>₺{getNetAmount(r).toLocaleString('tr-TR')}</td>
                        <td>{r.booking?.transportation || '—'}</td>
                        <td>{r.booking?.early_stay || '—'}</td>
                        <td>{r.booking?.checkout_day || '—'}</td>
                        <td>{r.booking?.roommate || '—'}</td>
                        <td>{r.booking?.emergency_contact_name || '—'}</td>
                        <td>{r.booking?.emergency_contact_phone || '—'}</td>
                        <td>{r.booking?.payment_method || '—'}</td>
                        <td>{r.booking?.kvkk_consent ? '✓' : '✗'}</td>
                        <td><span className={`badge bg-${sc.badge}`}>{sc.label}</span></td>
                        <td>
                          {r.booking?.payment_receipt_url
                            ? <a href={r.booking.payment_receipt_url} target="_blank" rel="noreferrer" className="btn btn-outline-secondary btn-sm py-0">Gör</a>
                            : '—'}
                        </td>
                        <td className="text-muted">{new Date(r.created_at).toLocaleDateString('tr-TR')}</td>
                        <td>
                          <div className="d-flex gap-1">
                            {showDeletedOnly ? (
                              <button className="btn btn-warning btn-sm py-0 text-dark" disabled={!!actionLoading} onClick={() => restoreRecord(r.order_id)}>Geri Getir</button>
                            ) : (
                              <>
                                {r.status !== 'approved' && (
                                  <button className="btn btn-success btn-sm py-0" disabled={!!actionLoading} onClick={() => updateStatus(r.order_id, 'approved')}>Onayla</button>
                                )}
                                {r.status !== 'declined' && (
                                  <button className="btn btn-danger btn-sm py-0" disabled={!!actionLoading} onClick={() => updateStatus(r.order_id, 'declined')}>Reddet</button>
                                )}
                              </>
                            )}
                            <button className="btn btn-outline-danger btn-sm py-0" disabled={!!actionLoading} onClick={() => deleteRecord(r.order_id)}>
                              {showDeletedOnly ? 'Kalıcı Sil' : 'Sil'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="card-footer text-muted small">{filtered.length} kayıt gösteriliyor</div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedRecord && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }} onClick={() => setSelectedRecord(null)}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#220d1c', color: '#fff' }}>
                <div>
                  <h5 className="modal-title mb-0">{b?.full_name || selectedRecord.customer_name}</h5>
                  <small style={{ opacity: 0.6 }}>{selectedRecord.order_id}</small>
                </div>
                <button className="btn-close btn-close-white" onClick={() => setSelectedRecord(null)} />
              </div>

              <div className="modal-body">

                {/* Section: Kişisel Bilgiler */}
                <h6 className="text-muted text-uppercase mb-2" style={{ fontSize: '0.72rem', letterSpacing: '0.08em' }}>Kişisel Bilgiler</h6>
                <div className="row g-2 mb-4">
                  <Field label="Ad Soyad"      value={b?.full_name} />
                  <Field label="E-posta"        value={b?.email || selectedRecord.customer_email} />
                  <Field label="Alt E-posta"    value={b?.alt_email} />
                  <Field label="Telefon"        value={b?.phone_number || selectedRecord.customer_phone} />
                  <Field label="TC / Pasaport"  value={b?.passport_id} />
                  <Field label="Kulüp"          value={b?.club_name} />
                  <Field label="Dönem Görevi"   value={b?.term_assignment} />
                </div>

                {/* Section: Konaklama */}
                <h6 className="text-muted text-uppercase mb-2" style={{ fontSize: '0.72rem', letterSpacing: '0.08em' }}>Konaklama & Ulaşım</h6>
                <div className="row g-2 mb-4">
                  <Field label="Paket"          value={b?.palace_choice} />
                  <Field label="Ulaşım"         value={b?.transportation} />
                  <Field label="Giriş Günü"     value={b?.early_stay} />
                  <Field label="Çıkış Günü"     value={b?.checkout_day} />
                  <Field label="Oda Arkadaşı"   value={b?.roommate} />
                </div>

                {/* Section: Acil Durum */}
                <h6 className="text-muted text-uppercase mb-2" style={{ fontSize: '0.72rem', letterSpacing: '0.08em' }}>Acil Durum</h6>
                <div className="row g-2 mb-4">
                  <Field label="Acil Kişi İsmi"    value={b?.emergency_contact_name} />
                  <Field label="Acil Kişi Telefon"  value={b?.emergency_contact_phone} />
                </div>

                {/* Section: Ödeme */}
                <h6 className="text-muted text-uppercase mb-2" style={{ fontSize: '0.72rem', letterSpacing: '0.08em' }}>Ödeme</h6>
                <div className="row g-2 mb-4">
                  <Field label="Tutar"          value={`₺${(selectedRecord.amount || 0).toLocaleString('tr-TR')} ${selectedRecord.currency || 'TRY'}`} />
                  <Field label="Ödeme Yöntemi"  value={b?.payment_method} />
                  <Field label="Durum"          value={STATUS_CONFIG[selectedRecord.status]?.label || selectedRecord.status} />
                  <Field label="KVKK Onayı"     value={b?.kvkk_consent ? '✓ Onaylandı' : '✗ Onaylanmadı'} />
                  {b?.additional_notes && (
                    <Field label="Ek Notlar" value={b.additional_notes} full />
                  )}
                  {b?.payment_receipt_url && (
                    <div className="col-12">
                      <div className="p-2 rounded" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                        <small className="text-muted d-block mb-1" style={{ fontSize: '0.7rem', textTransform: 'uppercase' }}>Dekont</small>
                        <a href={b.payment_receipt_url} target="_blank" rel="noreferrer" className="btn btn-outline-secondary btn-sm">
                          Dekontu Görüntüle →
                        </a>
                      </div>
                    </div>
                  )}
                </div>

                {/* Section: Sistem */}
                <h6 className="text-muted text-uppercase mb-2" style={{ fontSize: '0.72rem', letterSpacing: '0.08em' }}>Sistem</h6>
                <div className="row g-2">
                  <Field label="Sipariş No"     value={selectedRecord.order_id} />
                  <Field label="Kayıt Tarihi"   value={new Date(selectedRecord.created_at).toLocaleString('tr-TR')} />
                  <Field label="Son Güncelleme" value={new Date(selectedRecord.updated_at).toLocaleString('tr-TR')} />
                </div>

              </div>

              <div className="modal-footer">
                <button className="btn btn-success" disabled={!!actionLoading} onClick={() => updateStatus(selectedRecord.order_id, 'approved')}>Onayla</button>
                <button className="btn btn-danger" disabled={!!actionLoading} onClick={() => updateStatus(selectedRecord.order_id, 'declined')}>Reddet</button>
                <button className="btn btn-outline-secondary" onClick={() => setSelectedRecord(null)}>Kapat</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
